import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Plus, X, Loader2, Eye, EyeOff, Mail, Phone } from "lucide-react";
import { useState, useEffect } from "react";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { useInterestTags, useProfileInterests } from "@/hooks/useInterests";
import { useStudentSkills } from "@/hooks/useStudentSkills";
import { SkillSelector } from "@/components/SkillSelector";
import { toast } from "@/hooks/use-toast";

const objetivos = ["Jovem Aprendiz", "Estágio", "Primeiro emprego", "Assistente", "Analista Junior", "Mudança de área"];

const estadosBR = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA",
  "PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"
];

export default function AlunoPerfilEditar() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { profile, isLoading: profileLoading, updateProfile } = useProfile();
  const { data: allTags, isLoading: tagsLoading } = useInterestTags();
  const { interests, setInterests } = useProfileInterests();
  const { skills: savedSkills, setSkills: saveSkills, isLoading: skillsLoading } = useStudentSkills();

  const [nome, setNome] = useState("");
  const [phone, setPhone] = useState("");
  const [cidade, setCidade] = useState("");
  const [uf, setUf] = useState("");
  const [bio, setBio] = useState("");
  const [objetivo, setObjetivo] = useState("");
  const [visibilidade, setVisibilidade] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  useEffect(() => {
    if (profile) {
      setNome(profile.full_name || "");
      setPhone(profile.phone || "");
      setCidade(profile.city || "");
      setUf(profile.state || "");
      setBio(profile.bio || "");
      setObjetivo(profile.objective || "");
      setVisibilidade(profile.visibility_global || false);
    }
  }, [profile]);

  useEffect(() => {
    setSelectedTagIds(interests.map((i) => i.tag_id));
  }, [interests]);

  useEffect(() => {
    setSelectedSkills(savedSkills);
  }, [savedSkills]);

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : prev.length < 8
        ? [...prev, tagId]
        : prev
    );
  };

  const handleSave = async () => {
    if (!nome.trim()) {
      toast({ title: "Nome é obrigatório", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await updateProfile.mutateAsync({
        full_name: nome.trim(),
        phone: phone.replace(/\D/g, "") || null,
        city: cidade.trim() || null,
        state: uf || null,
        bio: bio.trim() || null,
        objective: objetivo || null,
        visibility_global: visibilidade,
        visibility_updated_at: visibilidade ? new Date().toISOString() : profile?.visibility_updated_at || null,
      });
      await setInterests.mutateAsync(selectedTagIds);
      await saveSkills.mutateAsync(selectedSkills);
      toast({ title: "Perfil salvo!", description: "Suas alterações foram salvas." });
      setTimeout(() => navigate("/aluno/perfil"), 500);
    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || profileLoading || tagsLoading || skillsLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/aluno/perfil")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading text-xl font-bold text-foreground">Editar Perfil</h1>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-6">
        {/* Nome */}
        <div className="space-y-2">
          <Label htmlFor="nome">Nome completo</Label>
          <Input id="nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Seu nome completo" />
        </div>

        {/* Email (read-only) */}
        <div className="space-y-2">
          <Label>E-mail</Label>
          <div className="flex items-center gap-2 bg-muted/50 rounded-md px-3 py-2 border border-input">
            <Mail className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{user?.email ?? "—"}</span>
          </div>
          <p className="text-xs text-muted-foreground">O e-mail não pode ser alterado por aqui.</p>
        </div>

        {/* Telefone/WhatsApp */}
        <div className="space-y-2">
          <Label htmlFor="phone">Telefone / WhatsApp</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input id="phone" type="tel" className="pl-10" placeholder="(11) 99999-9999"
              value={formatPhone(phone)}
              onChange={(e) => setPhone(e.target.value)} />
          </div>
          <p className="text-xs text-muted-foreground">Usado para contato via WhatsApp quando aprovado.</p>
        </div>

        {/* Cidade + UF */}
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2 space-y-2">
            <Label htmlFor="cidade">Cidade</Label>
            <Input id="cidade" value={cidade} onChange={(e) => setCidade(e.target.value)} placeholder="Sua cidade" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="uf">UF</Label>
            <Select value={uf} onValueChange={setUf}>
              <SelectTrigger>
                <SelectValue placeholder="UF" />
              </SelectTrigger>
              <SelectContent>
                {estadosBR.map((e) => (
                  <SelectItem key={e} value={e}>{e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bio */}
        <div className="space-y-2">
          <Label htmlFor="bio">Bio (sobre você)</Label>
          <Textarea id="bio" placeholder="Conte um pouco sobre você, sua história, o que te motiva..."
            value={bio} onChange={(e) => setBio(e.target.value)} rows={4} className="resize-none" maxLength={300} />
          <p className="text-xs text-muted-foreground">{bio.length}/300 caracteres</p>
        </div>

        {/* Objetivo */}
        <div className="space-y-2">
          <Label>Objetivo atual</Label>
          <div className="flex flex-wrap gap-2">
            {objetivos.map((obj) => (
              <Badge key={obj} variant={objetivo === obj ? "interest-selected" : "interest"}
                className="cursor-pointer" onClick={() => setObjetivo(objetivo === obj ? "" : obj)}>
                {objetivo === obj && <Check className="w-3 h-3" />}{obj}
              </Badge>
            ))}
          </div>
        </div>

        {/* Interesses from DB */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Interesses</Label>
            <span className="text-xs text-muted-foreground">{selectedTagIds.length}/8 selecionados</span>
          </div>

          {selectedTagIds.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedTagIds.map((tagId) => {
                const tag = allTags?.find((t) => t.id === tagId);
                return (
                  <Badge key={tagId} variant="interest-selected" className="cursor-pointer" onClick={() => toggleTag(tagId)}>
                    {tag?.name ?? tagId}<X className="w-3 h-3 ml-1" />
                  </Badge>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {(allTags ?? [])
              .filter((t) => !selectedTagIds.includes(t.id))
              .map((tag) => (
                <Badge key={tag.id} variant="interest" className="cursor-pointer" onClick={() => toggleTag(tag.id)}>
                  <Plus className="w-3 h-3" />{tag.name}
                </Badge>
              ))}
          </div>

          {(!allTags || allTags.length === 0) && (
            <p className="text-xs text-muted-foreground">Nenhum interesse disponível.</p>
          )}
        </div>

        {/* Habilidades (Skills) */}
        <div className="space-y-3">
          <Label>Minhas Habilidades</Label>
          <SkillSelector
            selectedSkills={selectedSkills}
            onChange={setSelectedSkills}
            maxSkills={12}
            label=""
          />
        </div>

        {/* Visibilidade para empresas */}
        <div className="bg-card rounded-2xl p-5 border border-border space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {visibilidade ? <Eye className="w-5 h-5 text-primary" /> : <EyeOff className="w-5 h-5 text-muted-foreground" />}
              <div>
                <p className="text-sm font-medium text-foreground">Visível para empresas</p>
                <p className="text-xs text-muted-foreground">
                  {visibilidade
                    ? "Seu perfil aparece no Banco de Talentos para empresas parceiras"
                    : "Seu perfil está oculto para empresas"}
                </p>
              </div>
            </div>
            <Switch
              checked={visibilidade}
              onCheckedChange={setVisibilidade}
              disabled={profile?.profile_status !== "ready"}
            />
          </div>
          {profile?.profile_status !== "ready" && (
            <p className="text-xs text-muted-foreground bg-accent/10 rounded-lg p-3 border border-accent/20">
              🔒 Para ativar a visibilidade, você precisa ter status "Apto". Complete seus sinais de carreira na aba Carreira.
            </p>
          )}
          {profile?.profile_status === "ready" && !visibilidade && (
            <p className="text-xs text-muted-foreground bg-accent/10 rounded-lg p-3 border border-accent/20">
              💡 Ative a visibilidade para que empresas parceiras possam encontrar você no Banco de Talentos e enviar oportunidades.
            </p>
          )}
        </div>

        {/* Save */}
        <div className="pt-4 pb-8">
          <Button variant="hero" size="xl" className="w-full" onClick={handleSave} disabled={saving}>
            {saving ? (<><Loader2 className="w-5 h-5 animate-spin" />Salvando...</>) : "Salvar alterações"}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
